import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import type { URLOpenListenerEvent } from '@capacitor/app';
import { App } from '@capacitor/app';
import { AuthService } from '@core/auth/services/auth.service';
import { NavController } from '@ionic/angular/standalone';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DeepLinkService {
  private readonly DEEP_LINK_STORAGE_KEY = 'deepLinkRedirect';

  private router = inject(Router);
  private authService = inject(AuthService);
  private navCtrl = inject(NavController);

  initialize(): void {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.handleDeepLink(event.url);
    });
  }

  getPendingRedirect(): {
    path: string;
    queryParams: Record<string, string>;
  } | null {
    try {
      const stored = localStorage.getItem(this.DEEP_LINK_STORAGE_KEY);
      if (!stored) {
        return null;
      }

      const redirectData = JSON.parse(stored);

      const isExpired = Date.now() - redirectData.timestamp > 3600000;
      if (isExpired) {
        this.clearPendingRedirect();
        return null;
      }

      return {
        path: redirectData.path,
        queryParams: redirectData.queryParams || {}
      };
    } catch {
      return null;
    }
  }

  clearPendingRedirect(): void {
    localStorage.removeItem(this.DEEP_LINK_STORAGE_KEY);
  }

  async handlePendingRedirect(): Promise<boolean> {
    const redirect = this.getPendingRedirect();

    if (!redirect) {
      return false;
    }

    this.clearPendingRedirect();
    await this.navigateToPath(redirect.path, redirect.queryParams);
    return true;
  }

  private async handleDeepLink(url: string): Promise<void> {
    const { path, queryParams } = this.parseUrl(url);

    await this.authService.waitForInitialization();

    const isAuthenticated = await firstValueFrom(this.authService.isAuthenticated);

    if (!isAuthenticated && this.requiresAuthentication(path)) {
      this.storePendingRedirect(path, queryParams);
      await this.navCtrl.navigateRoot('/auth');
      return;
    }

    await this.navigateToPath(path, queryParams);
  }

  private parseUrl(url: string): {
    path: string;
    queryParams: Record<string, string>;
  } {
    try {
      const urlObj = new URL(url);
      let path = '';

      if (urlObj.protocol === 'dhig:') {
        path = urlObj.host + urlObj.pathname;

        if (path && !path.startsWith('/')) {
          path = '/' + path;
        }
      } else {
        path = urlObj.pathname;
      }

      if (path.endsWith('/') && path.length > 1) {
        path = path.slice(0, -1);
      }

      if (!path || path === '/') {
        path = '/tile-menu';
      }

      const queryParams: Record<string, string> = {};
      urlObj.searchParams.forEach((value, key) => {
        queryParams[key] = value;
      });

      return { path, queryParams };
    } catch {
      return { path: '/tile-menu', queryParams: {} };
    }
  }

  private requiresAuthentication(path: string): boolean {
    const publicPaths = ['/auth', '/public', '/password-create', '/validation'];

    return !publicPaths.some((publicPath) => path.startsWith(publicPath));
  }

  private storePendingRedirect(path: string, queryParams: Record<string, string>): void {
    const redirectData = {
      path,
      queryParams,
      timestamp: Date.now()
    };
    localStorage.setItem(this.DEEP_LINK_STORAGE_KEY, JSON.stringify(redirectData));
  }

  private async navigateToPath(
    path: string,
    queryParams: Record<string, string> = {}
  ): Promise<void> {
    try {
      if (path !== '/tile-menu') {
        await this.navCtrl.navigateRoot('/tile-menu');

        const hasParams = Object.keys(queryParams).length > 0;
        if (hasParams) {
          await this.router.navigate([path], { queryParams });
        } else {
          await this.router.navigate([path]);
        }
      } else {
        await this.navCtrl.navigateRoot('/tile-menu');
      }
    } catch (error) {
      console.error('[DeepLink] Navigation error:', error);
      await this.navCtrl.navigateRoot('/tile-menu');
    }
  }
}
